import { coreToast } from "@verity/components/core";
import { useCallback } from "react";
import i18n from "../../../../../i18n";

export type RawNote = {
	id: number;
	content: string;
	timestamp: string | number; // Standard uses ISO string, RoomScan uses number
};

type NoteApiCall<TParams, TResponse> = (params: TParams) => Promise<TResponse>;

type NoteManagerConfig<T extends Date | number> = {
	sessionUuid: string;
	courseId: number;
	setter: (data: RawNote[]) => void;
	apis: {
		fetch: NoteApiCall<{ sessionId: string; courseId: number }, RawNote[]>;
		add: NoteApiCall<{ sessionId: string; courseId: number; content: string; timestamp: T }, boolean>;
		edit: NoteApiCall<{ sessionId: string; courseId: number; noteId: number; content: string }, boolean>;
		delete: NoteApiCall<{ sessionId: string; courseId: number; noteId: number }, boolean>;
	};
};

export default function useNoteManager<T extends Date | number>({
	sessionUuid,
	courseId,
	setter,
	apis,
}: NoteManagerConfig<T>) {
	const refresh = useCallback(async () => {
		try {
			const resp = await apis.fetch({ sessionId: sessionUuid, courseId });
			if (resp) setter(resp);
		} catch (e) {
			coreToast.error(i18n.t("result.components.studentSelection.videoPlayer.videoPlayerTSX.failGetNote"));
			console.error(e);
		}
	}, [sessionUuid, courseId, apis, setter]);

	const add = async (content: string, timestamp: T): Promise<boolean> => {
		const success = await apis.add({ sessionId: sessionUuid, courseId, content, timestamp });
		if (success) await refresh();
		return !!success;
	};

	const edit = async (noteId: number, content: string): Promise<boolean> => {
		const success = await apis.edit({ sessionId: sessionUuid, courseId, noteId, content });
		if (success) await refresh();
		return !!success;
	};

	const remove = async (noteId: number): Promise<boolean> => {
		const success = await apis.delete({ sessionId: sessionUuid, courseId, noteId });
		if (success) await refresh();
		return !!success;
	};

	return { refresh, add, edit, remove };
}
